// Shared composable consumed by every Membership Health Dashboard widget.
// Owns the read-side: summary, loading state, selected program filter, the
// growth-chart slice (for sparklines and other derived views), and the two
// URL builders that produce drill-down links to the Members report and the
// /memberships tabbed page.

import { type ComputedRef, computed } from "vue";
import { useStore } from "vuex";

import type {
  MembershipGrowthChartBucket,
  MembershipHealthSummary
} from "./types";

interface MembershipProgramSummary {
  id: number;
  name: string;
}

export interface UseMembershipDashboard {
  summary: ComputedRef<MembershipHealthSummary | null>;
  loading: ComputedRef<boolean>;
  selectedProgramIds: ComputedRef<number[]>;
  programs: ComputedRef<MembershipProgramSummary[]>;
  growthChart: ComputedRef<MembershipGrowthChartBucket[] | null>;
  membersReportHref: (baseQuery: string) => string | undefined;
  membershipsTabHref: (tabName: string) => string | undefined;
}

export function useMembershipDashboard(): UseMembershipDashboard {
  const store = useStore();

  const summary = computed<MembershipHealthSummary | null>(
    () => store.state.membershipDashboardSummary
  );
  const loading = computed<boolean>(
    () => store.state.membershipDashboardLoading || summary.value === null
  );
  const selectedProgramIds = computed<number[]>(
    () => store.state.membershipDashboardFilters.membership_program_ids ?? []
  );
  const programs = computed<MembershipProgramSummary[]>(
    () => store.state.membershipPrograms ?? []
  );
  const growthChart = computed<MembershipGrowthChartBucket[] | null>(
    () => store.state.membershipDashboardGrowthChart
  );

  function membersReportHref(baseQuery: string): string | undefined {
    if (selectedProgramIds.value.length > 1) return undefined;
    // `from_dashboard=1` is read by Reports/DatatableFilter.setInitialFilters
    // to suppress the Members report's default `membership_status=current`
    // filter, which would otherwise silently strip customers from the tile's
    // result set.
    if (selectedProgramIds.value.length === 1) {
      return `${baseQuery}&program=${selectedProgramIds.value[0]}&from_dashboard=1`;
    }
    return `${baseQuery}&from_dashboard=1`;
  }

  function membershipsTabHref(tabName: string): string | undefined {
    if (selectedProgramIds.value.length > 1) return undefined;
    const base = `/memberships?tab=memberships_${encodeURIComponent(tabName)}`;
    if (selectedProgramIds.value.length === 1) {
      return `${base}&program=${selectedProgramIds.value[0]}`;
    }
    return base;
  }

  return {
    summary,
    loading,
    selectedProgramIds,
    programs,
    growthChart,
    membersReportHref,
    membershipsTabHref
  };
}
